// @flow
import moment from 'moment';
import { toAtomicProgramNames } from '../../../utils/affordable';
import { preferencesObjectToArray } from './preferences';

// Edit and reinstate open on a saved waitlist record rather than on a
// prospect, so none of the prospect heuristics in ./prefill.js apply here:
// every value in the response is an answer that was actually given, and the
// job is only to translate wire names and wire types into the ones the
// controls hold. The key set matches buildDefaultValues exactly, so the two
// are interchangeable as react-hook-form defaultValues.

// react-hook-form treats undefined as "uncontrolled", which makes a control
// that later receives a value warn; every text-ish blank is '' instead.
const orBlank = (value: mixed): mixed => value ?? '';

// The component library's date controls are dayjs-backed: they paint nothing
// for a moment instance, and null is their own empty value.
const toDateOrNull = (value: ?string): ?Date =>
  value ? moment(value).toDate() : null;

// The radio groups store 'yes' / 'no'; an unanswered boolean has to stay
// visibly unanswered rather than default to No.
const toYesNo = (value: ?boolean): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return value ? 'yes' : 'no';
};

// The bedrooms multi-select holds strings on both sides, including the
// no-preference key, which arrives as a string already.
const toBedroomValue = (bedroom: number | string): string => String(bedroom);

// The one field where the response and the payload disagree: the record stores
// its preferences as a { [key]: boolean } map and the GET returns that map
// verbatim, while the PATCH schema — shared with convert — takes the ticked
// keys as an array. The multi-select only reads the array and throws
// `s.find is not a function` on the map, so the flattening happens here. An
// array is passed through untouched so this stays correct if the response is
// ever changed to send one.
const toPreferenceValues = (preferences: mixed): Array<string> => {
  if (Array.isArray(preferences)) {
    return preferences;
  }

  return preferencesObjectToArray(preferences);
};

export const buildEditDefaultValues = (waitlistDetails: ?Object): Object => {
  const applicant = waitlistDetails?.primaryApplicant;
  const mailingAddress = applicant?.mailingAddress;

  return {
    householdAnnualIncome: orBlank(waitlistDetails?.householdAnnualIncome),
    adults: orBlank(waitlistDetails?.adults),
    minors: orBlank(waitlistDetails?.minors),
    // A record saved before the table listed programs individually holds the
    // layered name whole ('LIHTC/HOME'), and the table checks a row by exact
    // name -- so it would render every layered row unticked and a toggle would
    // submit the combined name alongside the individual one. The backend splits
    // this too; splitting here as well is what keeps the prefill correct
    // whichever repo ships first.
    selectedPrograms: toAtomicProgramNames(waitlistDetails?.selectedPrograms),
    submissionDateTime: toDateOrNull(waitlistDetails?.submissionDateTime),
    firstName: orBlank(applicant?.firstName),
    middleName: orBlank(applicant?.middleName),
    lastName: orBlank(applicant?.lastName),
    suffix: orBlank(applicant?.suffixId),
    phoneNumber: orBlank(applicant?.phoneNumber),
    phoneType: orBlank(applicant?.phoneType),
    emailAddress: orBlank(applicant?.emailAddress),
    emailType: orBlank(applicant?.emailType),
    dateOfBirth: toDateOrNull(applicant?.dateOfBirth),
    gender: orBlank(applicant?.gender),
    multiRace: applicant?.multiRace ?? [],
    ethnicity: orBlank(applicant?.ethnicity),
    address: orBlank(mailingAddress?.street),
    city: orBlank(mailingAddress?.city),
    state: orBlank(mailingAddress?.stateId),
    zipCode: orBlank(mailingAddress?.zipCode),
    bedrooms: (waitlistDetails?.bedrooms ?? []).map(toBedroomValue),
    floorPlan: orBlank(waitlistDetails?.floorPlanId),
    requestAccommodation: toYesNo(waitlistDetails?.requestAccommodation),
    accommodationRequested: orBlank(waitlistDetails?.accommodationRequested),
    accessibleUnit: toYesNo(waitlistDetails?.accessibleUnit),
    displacementStatusCodeId: orBlank(
      waitlistDetails?.displacementStatusCodeId,
    ),
    preferences: toPreferenceValues(waitlistDetails?.preferences),
  };
};
